import React, { useEffect, useState } from "react";
import MasterAdminNavigation from "./navigation";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";

const AllClients = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ organizations: [] });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", organization: ""});
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API}/master/dashboard`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load master dashboard");
        return res.json();
      })
      .then((json) => setData(json))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/master/clients`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newClient),
      });
      if (!response.ok) throw new Error("Failed to create client");
      setShowAddDialog(false);
      setNewClient({ name: "", email: "", organization: ""});
      // Refresh data after adding new client
      const dashboardRes = await fetch(`${API}/master/dashboard`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      });
      if (!dashboardRes.ok) throw new Error("Failed to refresh dashboard");
      const dashboardData = await dashboardRes.json();
      setData(dashboardData);
    } catch (error) {
      console.error(error);
    }
  };
  if (loading) return <div className="p-6">Loading clients…</div>;
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/master/dashboard")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>
        <div className="mb-12 fade-in">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
                All Clients
              </h1>
              <p className="text-xl text-gray-600">
                View and manage all client organizations using ParkSmart.
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-vehicle-btn"
                  className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-full px-6 py-6"
                >
                  <Plus className="mr-2" size={20} />
                  Add Client
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-[#1A3C34]">Add New Client</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">
                      Name
                    </Label>
                    <Input
                      id="name"
                      data-testid="client-name"
                      placeholder="John Doe"
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Email
                    </Label>
                    <Input
                      id="email"
                      data-testid="client-email"
                      placeholder="john.doe@example.com"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization" className="text-gray-700 font-medium">
                      Organization
                    </Label>
                    <Input
                      id="organization"
                      data-testid="client-organization"
                      placeholder="ABC Organization"
                      value={newClient.organization}
                      onChange={(e) => setNewClient({ ...newClient, organization: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <Button
                    data-testid="submit-vehicle"
                    type="submit"
                    className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6"
                  >
                    Add Client
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Organization</th>
                <th className="px-4 py-2 text-right">Users</th>
                <th className="px-4 py-2 text-right">Vehicles</th>
                <th className="px-4 py-2 text-right">Bookings</th>
                <th className="px-4 py-2 text-right">Pending Carpools</th>
                <th className="px-4 py-2 text-right">Carpool Success %</th>
                <th className="px-4 py-2 text-right">Utilization %</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.organizations.map((org) => (
                <tr key={org.organization}>
                  <td className="px-4 py-2">
                    <Link to={`/master/clientDetails/${org.organization_id}`} className="text-[#1A3C34] font-medium hover:underline">
                      {org.organization}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-right">{org.total_users}</td>
                  <td className="px-4 py-2 text-right">{org.total_vehicles}</td>
                  <td className="px-4 py-2 text-right">{org.total_bookings}</td>
                  <td className="px-4 py-2 text-right">
                    {org.pending_carpool_requests}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {org.carpool_success_rate}%
                  </td>
                  <td className="px-4 py-2 text-right">
                    {org.utilization_percentage}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllClients;
